import { useEffect, useRef } from 'react';
import type {
  NegociacaoLoteStatus,
  NegociacaoLoteStatusEvent,
} from '@/features/negociacoes/types/loteStatus.types';
import { getStoredWorkspaceId } from '@/shared/utils/workspace';

const TERMINAL_STATUSES: NegociacaoLoteStatus[] = ['COMPLETED', 'FAILED', 'CANCELED'];

const isSseEnabled = process.env.NEXT_PUBLIC_ENABLE_SSE !== 'false';

interface UseNegociacaoLoteStatusEventsOptions {
  enabled?: boolean;
  onStatusChange?: (event: NegociacaoLoteStatusEvent) => void;
  onComplete?: (event: NegociacaoLoteStatusEvent) => void;
  onError?: (error: unknown) => void;
}

export function useNegociacaoLoteStatusEvents(
  loteId: string | null,
  options: UseNegociacaoLoteStatusEventsOptions = {},
) {
  const { enabled = true, onStatusChange, onComplete, onError } = options;

  const eventSourceRef = useRef<EventSource | null>(null);
  const lastStatusRef = useRef<NegociacaoLoteStatus | null>(null);
  const onStatusChangeRef = useRef(onStatusChange);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
  }, [onStatusChange, onComplete, onError]);

  useEffect(() => {
    if (!loteId || !enabled || !isSseEnabled) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      lastStatusRef.current = null;
      return;
    }

    const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8081/api';
    const url = new URL(
      `${apiBaseUrl}/negociacao/lotes/${loteId}/stream-status`,
      window.location.origin,
    );

    const workspaceId = getStoredWorkspaceId();
    if (workspaceId) {
      url.searchParams.set('workspaceId', workspaceId);
    }

    const eventSource = new EventSource(url.toString(), { withCredentials: true });
    eventSourceRef.current = eventSource;

    const handleStatus = (event: MessageEvent<string>) => {
      try {
        const payload = JSON.parse(event.data) as NegociacaoLoteStatusEvent;

        if (!payload.status) {
          return;
        }

        const status = payload.status;
        const previousStatus = lastStatusRef.current;

        onStatusChangeRef.current?.(payload);

        if (status !== previousStatus) {
          lastStatusRef.current = status;

          if (TERMINAL_STATUSES.includes(status)) {
            onCompleteRef.current?.(payload);
            eventSource.close();
            eventSourceRef.current = null;
          }
        }
      } catch (err) {
        console.error('[useNegociacaoLoteStatusEvents] Parse error:', err);
      }
    };

    const handleError = (err: unknown) => {
      onErrorRef.current?.(err);
    };

    eventSource.addEventListener('status', handleStatus as EventListener);
    eventSource.onerror = handleError;

    return () => {
      eventSource.removeEventListener('status', handleStatus as EventListener);
      eventSource.close();
      if (eventSourceRef.current === eventSource) {
        eventSourceRef.current = null;
      }
      lastStatusRef.current = null;
    };
  }, [loteId, enabled]);
}
